import { type FC, Fragment, type ReactElement, useEffect, useState } from 'react';
import { FileUpload, Modal } from '@/components';
import { Choose, If, Otherwise, When } from 'vite-plugin-react-control-statements';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import { EffectCoverflow } from 'swiper/modules';

import type { StudentMissionDetailResponse } from '@dopingtech/live_expert_bff_api_ts/api.ts';

import ClipboardIcon from '@/assets/svg/assignments-clipboard.svg';
import CloudFolderIcon from '@/assets/images/cloud-folder.png';
import InfoAlertIcon from '@/assets/images/information-alert.png';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore from 'swiper';
import { useSizes } from '@/utility/responsive.ts';
import LoadingSpinner from '@/components/loading-spinner';
import clsx from 'clsx';

type Props = {
	missionDetail?: StudentMissionDetailResponse;
	isLoading: boolean;
};

export const MissionDetail: FC<Props> = ({ missionDetail, isLoading }: Props): ReactElement<Props> => {
	const { isMobile } = useSizes();

	const [swiper, setSwiper] = useState<SwiperCore | null>(null);
	const [swiperActiveIndex, setSwiperActiveIndex] = useState<number>(0);
	const [openPreview, setOpenPreview] = useState<number>(-1);
	const [previewList, setPreviewList] = useState<Array<any> | undefined>([]);
	const [showPreloader, setShowPreloader] = useState<boolean>(false);

	const onShowPreview = (index: number, list: any): void => {
		setOpenPreview(index);
		setPreviewList(list);
	};

	const onHidePreview = (): void => {
		setOpenPreview(-1);
		setPreviewList([]);
	};

	useEffect(() => {
		if (openPreview !== -1 && swiper) {
			swiper.slideTo(openPreview);
			setSwiperActiveIndex(openPreview);
		}
	}, [openPreview, swiper]);

	return (
		<Fragment>
			<div className="mission-form-container">
				<div className="page-three preview">
					<h2 className="mission-form-header">Görev Detayı</h2>
					<div className="status-wrapper">
						<div className="expert-info">
							<figure className="profile-image">
								{/*{missionDetail?.expertProfilePicture ? (*/}
								{/*	<img*/}
								{/*		className="figure-image"*/}
								{/*		alt=""*/}
								{/*		src={missionDetail?.expertProfilePicture}*/}
								{/*	/>*/}
								{/*) : (*/}
								<i className="icons-user" />
								{/*)}*/}
							</figure>
							<div className="expert-info-detail">
								<span className="expert-name-label">Koç Adı</span>
								<span className="expert-name">{missionDetail?.expertName}</span>
							</div>
						</div>
						<span
							className={clsx('badge done', {
								progress: missionDetail?.status === 'IN_PROGRESS',
								idle: missionDetail?.status === 'TODO',
								done: missionDetail?.status === 'DONE'
							})}>
							<Choose>
								<When condition={missionDetail?.status === 'DONE'}>
									<span className="status-prefix">Durum:&nbsp;</span>
									Tamamlandı
									<span className="badge-icon">
										<i className="icons-pure-check" />
									</span>
								</When>
								<When condition={missionDetail?.status === 'IN_PROGRESS'}>
									<span className="status-prefix">Durum:&nbsp;</span>
									Devam Ediyor
									<span className="badge-icon">
										<i className="icons-hourglass-line" />
									</span>
								</When>
								<Otherwise>
									<span className="status-prefix">Durum:&nbsp;</span>
									Başlanmadı
									<span className="badge-icon">
										<span className="badge-icon">!</span>
									</span>
								</Otherwise>
							</Choose>
						</span>
					</div>
					<div className="preview-detail-wrapper readonly">
						<div className="preview-detail">
							<span className="preview-detail-label">Görev Adı</span>
							<span className="preview-detail-value">{missionDetail?.missionName}</span>
						</div>
						<div className="preview-detail">
							<span className="preview-detail-label">Son Teslim Tarihi</span>
							<span className="preview-detail-value">
								<i className="icons-calendar" />
								{dayjs(missionDetail?.dueDate).locale('tr')?.format('DD MMMM, dddd')}
							</span>
						</div>
					</div>
					<div className="preview-description-wrapper">
						<img
							alt=""
							className="description-icon"
							src={ClipboardIcon}
						/>
						<div className="preview-description">
							<span className="preview-description-label">Görev Detayları</span>
							<p className="preview-description-value">{missionDetail?.missionDescription}</p>
						</div>
					</div>
					<div className="preview-description-wrapper">
						<img
							alt=""
							className="description-icon"
							src={InfoAlertIcon}
						/>
						<div className="preview-description">
							<span className="preview-description-label">Öğrencinin Açıklaması</span>
							<p className="preview-description-value">
								<If condition={!missionDetail?.studentDescription}>
									<i className="icons-danger" />
								</If>
								{missionDetail?.status === 'DONE' ? missionDetail?.studentDescription : 'Öğrenciniz görevine açıklama eklememiştir'}
							</p>
						</div>
					</div>
					<If condition={!!missionDetail?.missionAttachmentList?.length}>
						<div className="preview-attachment-wrapper">
							<img
								alt=""
								className="description-icon"
								src={CloudFolderIcon}
							/>
							<div className="preview-description">
								<span className="preview-description-label">Görev Ekleri</span>
								<div className="upload-container">
									{missionDetail?.missionAttachmentList?.map((item, index) => (
										<FileUpload
											key={index}
											containerClassName="mission-file-upload-preview"
											currentFile={{ previewUrl: item?.url } as any}
											onShowPreview={() => onShowPreview(index, missionDetail?.missionAttachmentList)}
											previewMode={true}
											iconThumb={true}
											isDownload={false}
										/>
									))}
								</div>
							</div>
						</div>
					</If>
					<div className="preview-attachment-wrapper">
						<img
							alt=""
							className="description-icon"
							src={CloudFolderIcon}
						/>
						<div className="preview-description">
							<span className="preview-description-label">Belgeler</span>
							{missionDetail?.status === 'DONE' && missionDetail?.studentAttachmentList?.length ? (
								<div className="upload-container">
									{missionDetail?.studentAttachmentList?.map((item, index) => (
										<FileUpload
											key={`student-${index}`}
											containerClassName="mission-file-upload-preview"
											currentFile={{ previewUrl: item?.url } as any}
											onShowPreview={() => onShowPreview(index, missionDetail?.studentAttachmentList)}
											previewMode={true}
											iconThumb={true}
											isDownload={false}
										/>
									))}
								</div>
							) : (
								<p className="preview-description-value">
									<i className="icons-danger" />
									Öğrenciniz görevine belge yüklememiştir
								</p>
							)}
						</div>
					</div>
				</div>
				<If condition={isLoading}>
					<LoadingSpinner loadingText={'Yükleniyor'} />
				</If>
			</div>
			<Modal
				isOpen={openPreview !== -1}
				onClose={onHidePreview}
				closeOnEsc={true}
				closeOnClickOutside={false}
				size={isMobile ? 'full' : 'xl'}
				className="mission-preview-modal">
				<div className="mission-modal-document-preview">
					<a
						className="close-button"
						onClick={onHidePreview}>
						<i className="icons-close-circle" />
					</a>
					<div className="document-preview-container">
						<Swiper
							key={`swiper-${isMobile ? 'mobile' : 'desktop'}`}
							onSwiper={setSwiper}
							allowTouchMove={false}
							preventClicks={false}
							simulateTouch={false}
							touchStartPreventDefault={false}
							preventClicksPropagation={false}
							slidesPerView={1.5}
							centeredSlides={true}
							onActiveIndexChange={swiperInstance => setSwiperActiveIndex(swiperInstance.activeIndex)}
							effect="coverflow"
							coverflowEffect={{
								rotate: 0,
								scale: isMobile ? 1 : 0.9,
								stretch: 1,
								modifier: 1,
								slideShadows: false
							}}
							modules={[EffectCoverflow]}
							spaceBetween={isMobile ? 35 : 15}
							className="appointment-swiper swiper-no-swiping">
							{previewList?.map((item, index) => (
								<SwiperSlide key={index}>
									<FileUpload
										containerClassName="mission-file-upload-preview"
										currentFile={{ previewUrl: item?.url } as any}
										previewMode={true}
										isDownload={true}
										onPreload={setShowPreloader}
									/>
								</SwiperSlide>
							))}
						</Swiper>
						<div className="swiper-navigate-buttons">
							<button
								disabled={!swiperActiveIndex}
								className="btn"
								type="button"
								onClick={() => swiper?.slidePrev()}>
								<i className="icons-arrow-left" />
								Önceki
							</button>
							<button
								disabled={swiperActiveIndex === (previewList?.length as any) - 1}
								className="btn"
								type="button"
								onClick={() => swiper?.slideNext()}>
								Sonraki
								<i className="icons-arrow-right" />
							</button>
						</div>
					</div>
				</div>
				<If condition={showPreloader}>
					<LoadingSpinner loadingText={'Yükleniyor'} />
				</If>
			</Modal>
		</Fragment>
	);
};
